import { observer } from "mobx-react-lite"
import { UIEvent, useLayoutEffect } from "react"
import { useTranslation } from "react-i18next"
import { navigate } from "wouter/use-browser-location"
import { useStore } from "../../../contexts/store"
import { ConversationItem } from "../../../shared/src/types/Miscs"
import ConversationListItem from "./ConversationListItem"
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material"
import ConversationIcon from '@mui/icons-material/QuestionAnswer'

const ConversationList = ({podId, activeConversationId}: {podId:string, activeConversationId: string | undefined}) => {
  const { t } = useTranslation()
  const { uiStore, sessionStore, podStore } = useStore()
  const deviceWidth = uiStore.deviceWidth
  const showUnreads = uiStore.showUnreads
  // load list of conversations in which the user is involved

  useLayoutEffect(() => {
    const scrollPos = uiStore.getPodScrollPos(podId, "conversations")
    const podContainer = document.getElementById("conversation-list")
    if(scrollPos !== null && podContainer) {
      podContainer.scroll({
        top: scrollPos
      })
    }
  })

  if (!sessionStore.session) return null
  const userId = sessionStore.session.user.userId
  let conversationList = podStore.getConversations(userId)

  if(!userId || conversationList === null) return null

  // sort conversation list by time of last written message
  conversationList.sort((a: ConversationItem, b: ConversationItem) => {
    if (a.tLastMessage > b.tLastMessage) return -1
    if (a.tLastMessage < b.tLastMessage) return 1
    return 0
  })
  // when selected show unread
  if(showUnreads) {
    conversationList = conversationList.filter(conversation => conversation.hasUnreadMessages || conversation.interactionId === activeConversationId)
  }

  const changeActiveConversation = (interactionId: string | null) => {
    const currentLocation = window.location.pathname
    uiStore.setActiveConversation(podId, interactionId)
    uiStore.setEditedMessageId(null)
    const url = `/pod/${podId}/conversations${interactionId ? `/${interactionId}` : ""}`
    navigate(url, {replace: true, state: {prevUrl: currentLocation}})
  }

  const updateScrollPos = (event: UIEvent<HTMLDivElement>) => {
    if(event && event.currentTarget && event.currentTarget.scrollTop !== undefined) {
      const scrollTop = event.currentTarget.scrollTop
      uiStore.setPodScrollPos(podId, "conversations", scrollTop)
    }
  }

  const UnreadsButton = () => (
    <ToggleButtonGroup
      color="primary"
      value={showUnreads ? "unreads" : "all"}
      exclusive
      onChange={(_, newAlignment: string) => {newAlignment === "unreads" ? uiStore.setShowUnreads(true) : uiStore.setShowUnreads(false)}}
      aria-label="change between all und unread messages"
      size="small"
      sx={{alignItems:"center", gap: "5px", margin: "10px 15px"}}
    >
      {deviceWidth !== "tiny" &&
        <ConversationIcon sx={{margin: "0 10px"}} fontSize="small" color="primary" />
      }
      <ToggleButton sx={{border: "none", fontSize: "18px", textTransform: "unset"}} value={"all"}>{t('Conversations')}</ToggleButton>
      <ToggleButton sx={{border: "none", fontSize: "18px", textTransform: "unset"}}  value={"unreads"}>{t('Unreads')}</ToggleButton>
    </ToggleButtonGroup>
  )

  return (
    <Box className="overflowWrapper" sx={{alignContent: "start", display: "grid", borderRight: "1px solid rgba(63, 67, 80, 0.16)"}}>
      {/* List Header */}
        <Box sx={{
            borderBottom: "1px solid rgba(63, 67, 80, 0.16)"
          }}>
            <UnreadsButton />
        </Box>
      {/* List Content */}
      <Box id="conversation-list" onScroll={(event) => updateScrollPos(event)} sx={{display: "grid", overflowY: "auto"}}>
        {conversationList.map((conversation) =>
          <Box key={conversation.interactionId} onClick={() => changeActiveConversation(conversation.interactionId)}>
            <ConversationListItem item={conversation} podId={podId} activeConversationId={activeConversationId} />
          </Box>
        )}
        {conversationList.length === 0 &&
          <Box sx={{display: "grid", justifyContent: "center", color: "#9a9a9a", marginTop: "20px"}}>
            {t('no items existing')}
          </Box>
        }
      </Box>
    </Box>
  )
}


export default observer(ConversationList)
